import {observer} from "mobx-react";
import {mapSystem, VhsEvent, vhsUserStore} from "../../../store";
import {dateFormat} from "../../../utils";
import {SourceSystemDto} from "../../../api/vhs";
import {convertDocumentType, mapDocumentTypeStatus} from "../../../api";
import {useEffect, useRef} from "react";
import {Linq} from "../../ui";

interface VhsDetailItemProps {
    data: VhsEvent
    excluded: boolean
    first: boolean
    pos: 'odd' | 'even'
}

function hasLink(system: SourceSystemDto): boolean {
    return system !== 'VRS'
}

function backgroundColor(system: SourceSystemDto): string {
    switch (system) {
        case SourceSystemDto.SEA:
            return 'has-background-warning-40'
        case SourceSystemDto.VDA:
            return 'has-background-success'
        case SourceSystemDto.VRS:
            return 'has-background-primary'
        case SourceSystemDto.SLS:
            return 'has-background-info'
        case SourceSystemDto.FMS:
            return 'has-background-warning-70'
        case SourceSystemDto.PMS:
            return 'has-background-primary'
    }
}

function textColor(system: SourceSystemDto): string {
    switch (system) {
        case SourceSystemDto.SEA:
            return 'has-text-primary-invert'
        case SourceSystemDto.VDA:
            return 'has-text-primary-invert'
        case SourceSystemDto.VRS:
            return 'has-text-primary-invert'
        case SourceSystemDto.SLS:
            return 'has-text-primary-invert'
        case SourceSystemDto.FMS:
            return 'has-text-primary-invert'
        case SourceSystemDto.PMS:
            return 'has-text-primary-invert'
    }
}

function makeLink(event: VhsEvent) {
    const {
        system,
        systemId,
        vin,
        documentType
    } = event
    const parts: string[] = [
        `/${system.toLowerCase()}`
    ]

    if (system === SourceSystemDto.FMS) {
        parts.push(`/packages`)
    }

    if (system !== SourceSystemDto.SEA) {
        parts.push(`/${systemId}`)
    }

    if (system === SourceSystemDto.SEA) {
        parts.push(`/document`)
    }

    const urlPath = parts.join('')

    if (system === SourceSystemDto.SEA) {
        return `${urlPath}?q=${vin}&type=${documentType}&f=on`
    }

    return urlPath
}

const VhsDetailItem = observer(function VhsDetailItem({
                                                          data,
                                                          first,
                                                          excluded,
                                                          pos,
                                                      }: VhsDetailItemProps) {


    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    vhsUserStore.fetchUserByUsername(data.username).finally()
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            }
        );

        const currentRef = containerRef.current;

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [data])

    const even = pos === 'even'
    const odd = pos === 'odd'
    const {
        id,
        system,
        systemEventType,
        documentType,
        username,
        documentId,
        documentLocation,
        corNumber,
        licensePlate,
        lastScan,
        createdDate
    } = data
    const anythingForContent = Boolean(
        username ||
        documentId ||
        documentLocation ||
        lastScan ||
        licensePlate ||
        corNumber
    )
    const itemLabel = `${mapSystem(system)}: ${documentType ? convertDocumentType(documentType) : ''}`
    return <div
        ref={containerRef}
        data-id={id}
        style={{
            padding: '0',
            width: '50%',
            fontSize: '0.9em',
            alignSelf: even ? 'flex-start' : 'flex-end',
            borderRight: even ? '1px dashed black' : undefined,
            borderLeft: odd ? '1px dashed black' : undefined,
            marginTop: odd && anythingForContent ? '-4.0em' : undefined,
            marginBottom: odd && anythingForContent ? '-4.3em' : undefined,
            marginRight: odd ? '1px' : undefined,
            display: 'flex',
            justifyContent: even ? 'end' : undefined,
        }}>
        <div style={{
            display: 'flex',
            alignItems: 'center'
        }}>
            <div style={{
                order: even ? '1' : undefined,
                flex: '0',
                padding: '0 1em 0.3em 1em',
                textWrap: 'nowrap',
                borderBottom: '1px dashed black'
            }}>
                <>{dateFormat(createdDate.toISOString())}</>
            </div>
            <div style={{flex: '1', paddingTop: '1em'}}>
                <div
                    className="card"
                    style={{
                        marginTop: first ? '0' : '0em',
                        marginBottom: '0em',
                        filter: excluded ? 'grayscale(1)' : undefined,
                        opacity: excluded ? '0.3' : undefined
                    }}
                >
                    <header
                        className={`card-header ${backgroundColor(system)}  ${textColor(system)}`}
                    >
                        <p className={`card-header-title ${textColor(system)}`}>
                            {itemLabel}
                        </p>
                        <span className={'card-header-icon'} style={{cursor: 'default'}}>
                            <span
                                className="tag is-light is-rounded has-text-primary">{mapDocumentTypeStatus(systemEventType)}</span>
                        </span>
                        {hasLink(system) ? <Linq
                            className={'card-header-icon'}
                            to={makeLink(data)}><span
                            className="material-symbols-outlined">link</span></Linq> : <></>}
                    </header>
                    {
                        anythingForContent &&
                        <div className="card-content">
                            <div className="content">
                                {documentId && <p><strong>Dokumenten-Id:</strong> {documentId}</p>}
                                {documentLocation && <p><strong>Lagerort:</strong> {documentLocation}</p>}
                                {licensePlate && <p><strong>Nummernschild:</strong> {licensePlate}</p>}
                                {corNumber && <p><strong>ZBII-Nummer:</strong> {corNumber}</p>}
                                {lastScan && <p><strong>Last-Scan:</strong> {dateFormat(lastScan.toISOString())}</p>}
                                {username && <p><strong>Benutzer:</strong> {vhsUserStore.getDisplayName(username)}</p>}
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
})

export {
    VhsDetailItem
}
